import {
  TextField,
  PasswordField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  Alert,
  InfoTooltip,
  Flex,
} from '@elementary/common-ui/components';
import { UseFormReturn } from 'react-hook-form';
import { HexForm } from 'shared/types';
import styled from 'styled-components';

const Link = styled.a`
  color: ${({ theme }) => theme.palette.accent.primary};
`;

type Props = {
  formMethods: UseFormReturn<HexForm>;
};

const HexDialogForm = ({ formMethods }: Props) => {
  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = formMethods;

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Hex integration is currently only supported for BigQuery and Snowflake.
      </Alert>

      <FormField
        title="Connection name"
        errorMessage={errors.hexConnectionName?.message as string}
      >
        <TextField
          {...register('hexConnectionName', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="My Hex connection"
          inputProps={{ maxLength: 50 }}
          size="large"
        />
      </FormField>

      <FormField
        title={
          <Flex gap={1} alignItems="center">
            Workspace token
            <InfoTooltip
              size={16}
              title={
                <>
                  Hex Workspace token with read access for all categories and
                  &ldquo;read project queried tables&rdquo;. Learn more{' '}
                  <Link
                    href="https://learn.hex.tech/docs/api/api-overview#workspace-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here.
                  </Link>{' '}
                </>
              }
            />
          </Flex>
        }
        errorMessage={errors.hexWorkspaceToken?.message as string}
      >
        <PasswordField
          {...register('hexWorkspaceToken', {
            required: requiredLabel,
          })}
          placeholder="1b5fc425..."
          isFormValueDirty={dirtyFields.hexWorkspaceToken}
          clearFormValue={() => setValue('hexWorkspaceToken', '')}
          size="large"
        />
      </FormField>

      <FormField
        title={
          <Flex gap={1} alignItems="center">
            Custom URL
            <InfoTooltip
              size={16}
              title={
                <>
                  Leave empty to use the default Hex API URL. Learn more{' '}
                  <Link
                    href="https://learn.hex.tech/docs/api/api-overview#setup-needed-for-the-api"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here.
                  </Link>{' '}
                </>
              }
            />
          </Flex>
        }
        errorMessage={errors.hexCustomUrl?.message as string}
        optional
      >
        <TextField
          {...register('hexCustomUrl', {
            pattern: {
              value: /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
              message:
                'Custom URL should be in subdomain format (e.g., eu.hex.tech)',
            },
          })}
          variant="outlined"
          placeholder="app.hex.tech"
          size="large"
        />
      </FormField>
    </>
  );
};

export default HexDialogForm;
